/**
 * Tool Usage resource - /api/tool-usage endpoints
 */
import { getLogger } from '@qwickapps/logging';
import { ExecutionStatus } from '@qwickforge/api';
const log = getLogger('QwickForge.MiddlewareClient.ToolUsageResource');
export class ToolUsageResource {
    http;
    writeBuffer;
    constructor(http, writeBuffer) {
        this.http = http;
        this.writeBuffer = writeBuffer;
    }
    /**
     * Create a new tool usage record
     */
    async create(executionId, toolName, input, metadata) {
        const toolUsageId = crypto.randomUUID();
        const now = new Date();
        log.debug('Creating tool usage', { toolUsageId, executionId, toolName });
        if (this.writeBuffer) {
            // Add to write buffer
            this.writeBuffer.add({
                type: 'tool_usage',
                data: {
                    id: toolUsageId,
                    executionId,
                    toolName,
                    input,
                    output: null,
                    startedAt: now.toISOString(),
                    completedAt: null,
                    status: ExecutionStatus.PENDING,
                    errorMessage: null,
                    metadata: metadata || null,
                    createdAt: now.toISOString(),
                },
                timestamp: Date.now(),
            });
        }
        else {
            // Direct write
            await this.http.post('/api/tool-usage', {
                id: toolUsageId,
                executionId,
                toolName,
                input,
                metadata,
            });
        }
        log.debug('Tool usage created', { toolUsageId, toolName });
        // Return minimal tool usage object
        return {
            id: toolUsageId,
            executionId,
            orgId: '',
            toolName,
            input,
            output: null,
            startedAt: now,
            completedAt: null,
            status: ExecutionStatus.PENDING,
            errorMessage: null,
            metadata: metadata || null,
            createdAt: now,
        };
    }
    /**
     * Record tool execution result
     */
    async recordExecution(record) {
        log.debug('Recording tool execution', {
            toolUsageId: record.toolUsageId,
            toolName: record.toolName,
            success: record.success,
        });
        if (this.writeBuffer) {
            this.writeBuffer.add({
                type: 'tool_usage',
                data: {
                    id: record.toolUsageId,
                    executionId: record.executionId,
                    toolName: record.toolName,
                    input: record.input,
                    output: record.output,
                    startedAt: record.startedAt.toISOString(),
                    completedAt: record.completedAt.toISOString(),
                    status: record.success ? ExecutionStatus.COMPLETED : ExecutionStatus.FAILED,
                    errorMessage: record.errorMessage || null,
                },
                timestamp: Date.now(),
            });
        }
        else {
            await this.http.patch(`/api/tool-usage/${record.toolUsageId}`, {
                output: record.output,
                completedAt: record.completedAt.toISOString(),
                status: record.success ? ExecutionStatus.COMPLETED : ExecutionStatus.FAILED,
                errorMessage: record.errorMessage || null,
            });
        }
        log.debug('Tool execution recorded', {
            toolUsageId: record.toolUsageId,
            success: record.success,
        });
    }
    /**
     * Get tool usage by ID
     */
    async get(toolUsageId) {
        try {
            log.debug('Getting tool usage', { toolUsageId });
            const toolUsage = await this.http.get(`/api/tool-usage/${toolUsageId}`);
            return toolUsage;
        }
        catch (error) {
            if (error.statusCode === 404) {
                log.debug('Tool usage not found', { toolUsageId });
                return undefined;
            }
            throw error;
        }
    }
    /**
     * List tool usage for an execution
     */
    async listByExecution(executionId) {
        log.debug('Listing tool usage for execution', { executionId });
        const toolUsage = await this.http.get(`/api/executions/${executionId}/tool-usage`);
        log.debug('Tool usage listed', { count: toolUsage.length });
        return toolUsage;
    }
}
//# sourceMappingURL=ToolUsageResource.js.map